import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { invoiceAPI } from 'redux/invoiceAPI';
import { FetchingState } from '@payer-admin-ui/components-v2/src/types/state';
import { FrontendSpecificPayWithCreditResponse } from '@payer-admin-ui/api/src/frontendspecific/responses/FrontendSpecificPayWithCreditResponse';
import { PayWithCreditInvoiceRequest } from '@payer-admin-ui/api/src/frontendspecific/requests/PayWithCreditInvoiceRequest';
import { ResponseInvoiceV2 } from '@payer-admin-ui/api/src/invoices/models/ResponseInvoiceV2';
import { InternalInvoiceResponseV3 } from '@payer-admin-ui/api/src/invoices/responses/InvoiceResponseV3';
import { InvoiceType } from '@payer-admin-ui/api/src/invoices/models/InvoiceType';
import { PaymentStatus } from '@payer-admin-ui/api/src/invoices/models/PaymentStatus';
import { showSnackbar } from 'redux/snackbar';
import i18n from 'i18next';
import { fromCents } from '@payer-admin-ui/components-v2/src/utils/currency';
import { type InvoiceFilters, emptyInvoiceFilters, fetchInvoice } from '../invoicesGlobalState';

export const searchInvoices = createAsyncThunk<InternalInvoiceResponseV3[], void, { state: any }>(
  'invoices/actions/PairOffModal/searchInvoices',
  async (_, { getState }) => {
    const state: any = getState();
    const { query, appliedFilters } = state.invoices.actions.PairOffModal.search;
    const trimmed = (query || '').trim();
    const filters: InvoiceFilters = {
      ...appliedFilters,
      search: trimmed,
    };
    const response = await invoiceAPI.fetchPage({ page: 0, pageSize: 50, filters, sort: [] });
    return response.contents;
  },
);

export const pairOff = createAsyncThunk<FrontendSpecificPayWithCreditResponse, PayWithCreditInvoiceRequest>(
  'invoices/actions/pairOff',
  async ({ creditInvoiceNumber, toPayInvoiceNumber, amount }, { dispatch }) => {
    const result = await invoiceAPI.actions.pairOff({ creditInvoiceNumber, toPayInvoiceNumber, amount });
    dispatch(fetchInvoice({ invoiceNumber: creditInvoiceNumber }));
    dispatch(fetchInvoice({ invoiceNumber: toPayInvoiceNumber }));
    dispatch(
      showSnackbar(
        i18n.t('invoices.actions.pairOff.success', {
          creditInvoiceNumber,
          toPayInvoiceNumber,
          amount: fromCents(amount),
        }),
        {
          severity: 'success',
        },
      ),
    );
    return result;
  },
);

export type PairOffModalInvoiceState = {
  invoice?: InternalInvoiceResponseV3 | ResponseInvoiceV2;
} & FetchingState;

export type PairOffModalState = {
  pairOff: {
    isFetching: boolean;
    error: any;
    success: boolean;
    selectedInvoice: InternalInvoiceResponseV3 | null;
  };
  search: {
    query: string;
    isFetching: boolean;
    error: any;
    results: InternalInvoiceResponseV3[];
    filters: InvoiceFilters;
    appliedFilters: InvoiceFilters;
  };
};

const initialInvoiceFilters: InvoiceFilters = {
  ...emptyInvoiceFilters,
  invoiceType: [InvoiceType.NORMAL, InvoiceType.CASH, InvoiceType.ESCROW, InvoiceType.PREPAYMENT],
  paymentStatus: [PaymentStatus.UNPAID, PaymentStatus.PARTIALLY_PAID],
};

const initialState: PairOffModalState = {
  pairOff: {
    isFetching: false,
    error: null,
    success: false,
    selectedInvoice: null,
  },
  search: {
    query: '',
    isFetching: false,
    error: null,
    results: [],
    filters: {
      ...initialInvoiceFilters,
    },
    appliedFilters: {
      ...initialInvoiceFilters,
    },
  },
};

const pairOffInvoiceModalSlice = createSlice({
  name: 'invoices/actions/PairOffModal',
  initialState,
  reducers: {
    reset: () => initialState,
    setSearchQuery: (state, { payload }: PayloadAction<string>) => {
      state.search.query = payload;
    },
    setSearchFilters: (state, { payload }: PayloadAction<Partial<InvoiceFilters>>) => {
      state.search.filters = { ...state.search.filters, ...payload } as InvoiceFilters;
    },
    clearSearchFilter: (state, { payload }: PayloadAction<keyof InvoiceFilters>) => {
      state.search.filters = { ...state.search.filters, [payload]: null } as InvoiceFilters;
    },
    clearAllSearchFilters: state => {
      state.search.filters = {
        ...initialInvoiceFilters,
      };
      state.search.appliedFilters = state.search.filters;
    },
    applySearchFilters: state => {
      state.search.appliedFilters = {
        ...state.search.filters,
      };
    },
    setSelectedInvoice: (state, { payload }: PayloadAction<InternalInvoiceResponseV3>) => {
      state.pairOff.selectedInvoice = payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(pairOff.pending, (state, { meta: { arg } }) => {
      state.pairOff.isFetching = true;
      state.pairOff.error = null;
    });
    builder.addCase(pairOff.fulfilled, (state, { meta: { arg }, payload: { creditInvoice, toPayInvoice } }) => {
      state.pairOff.isFetching = false;
      state.pairOff.success = true;
    });
    builder.addCase(pairOff.rejected, (state, { meta: { arg }, error }) => {
      state.pairOff.isFetching = false;
      state.pairOff.success = false;
      state.pairOff.error = error;
    });

    builder.addCase(searchInvoices.pending, state => {
      state.search.isFetching = true;
      state.search.error = null;
    });
    builder.addCase(searchInvoices.fulfilled, (state, { payload }) => {
      state.search.isFetching = false;
      state.search.results = payload;
    });
    builder.addCase(searchInvoices.rejected, (state, { error }) => {
      state.search.isFetching = false;
      state.search.error = error;
    });
  },
});

export const {
  actions: {
    reset,
    setSearchQuery,
    setSearchFilters,
    clearSearchFilter,
    clearAllSearchFilters,
    applySearchFilters,
    setSelectedInvoice,
  },
  reducer: invoiceActionPairOffModalReducer,
} = pairOffInvoiceModalSlice;
